import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';

import { useNavigate, Link } from 'react-router-dom';
import {
  FaUser, FaEnvelope, FaLock,
  FaSignInAlt, FaGraduationCap, FaShieldAlt, FaCheckCircle, FaPaperPlane
} from 'react-icons/fa';



const Register = () => {
  const [step, setStep] = useState(1); // 1 = form, 2 = otp, 3 = success
  const [formData, setFormData] = useState({ name: '', email: '', password: '', securityQuestion: '', securityAnswer: '' });
  const [otpInput, setOtpInput] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const otpRefs = useRef([]);
  const otpSendId = useRef(0);
  const isVerifyingOtp = useRef(false);

  // ── Password strength rules ───────────────────────────────────────────────
  const passwordRules = [
    { label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { label: 'One uppercase letter (A-Z)', test: (p) => /[A-Z]/.test(p) },
    { label: 'One number (0-9)', test: (p) => /[0-9]/.test(p) },
    { label: 'One special character (!@#$…)', test: (p) => /[^A-Za-z0-9]/.test(p) },
  ];
  const isPasswordStrong = passwordRules.every(r => r.test(formData.password));

  const { register, sendOtp } = useAuth();

  const navigate = useNavigate();

  // ── Step 1: Send OTP ──────────────────────────────────────────────────────
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (isSendingOtp) return;
    setError('');

    if (!isPasswordStrong) {
      setError('Password does not meet the requirements listed below.');
      return;
    }

    setIsSendingOtp(true);
    const sendId = ++otpSendId.current;

    const result = await sendOtp(formData.name, formData.email);

    if (sendId !== otpSendId.current) return;

    setIsSendingOtp(false);
    
    if (result.success) {
      setStep(2);
    } else {
      setError(result.message);
    }
  };


  // ── OTP input helpers ─────────────────────────────────────────────────────
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const updated = [...otpInput];
    updated[index] = value.slice(-1);
    setOtpInput(updated);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpInput[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) {
      setOtpInput(text.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const resendOtp = async () => {
    if (isSendingOtp) return;
    setError('');
    setOtpInput(['', '', '', '', '', '']);
    setIsSendingOtp(true);
    const sendId = ++otpSendId.current;
    
    const result = await sendOtp(formData.name, formData.email);
    
    if (sendId !== otpSendId.current) return;

    setIsSendingOtp(false);
    if (!result.success) {
      setError(result.message);
    }
  };


  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    if (isVerifyingOtp.current) return;

    const enteredOtp = otpInput.join('');

    if (enteredOtp.length !== 6) {
      setError('Please enter the full 6-digit code.');
      return;
    }

    isVerifyingOtp.current = true;
    setIsLoading(true);
    setError('');

    const result = await register(
      formData.name, 
      formData.email, 
      formData.password, 
      'student',
      formData.securityQuestion,
      formData.securityAnswer,
      enteredOtp
    );


    if (!result.success) {
      setError(result.message);
      setIsLoading(false);
      isVerifyingOtp.current = false;
      return;
    }

    // Welcome email is now handled by the server (if configured) or non-critical


    setIsLoading(false);
    setStep(3);

    // Redirect after a short celebration delay
    setTimeout(() => navigate('/lessons'), 2500);
  };

  // ── Shared background ─────────────────────────────────────────────────────
  const background = (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-gold/5 blur-[120px] rounded-full"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-gold/5 blur-[120px] rounded-full"></div>
    </div>
  );

  // ── Step 3: Success Screen ────────────────────────────────────────────────
  if (step === 3) {
    return (
      <div className="min-h-screen bg-maroon-dark flex items-center justify-center p-4 pt-24 relative overflow-hidden">
        {background}
        <div className="w-full max-w-md relative z-10 text-center">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-10 border border-gold/10 shadow-2xl">
            <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <FaCheckCircle className="text-white text-4xl" />
            </div>
            <h2 className="text-3xl font-serif font-bold text-gold-light mb-3">Welcome to Taalsetu!</h2>
            <p className="text-gold-light/60 font-sans mb-2">
              Your account has been created successfully.
            </p>
            <p className="text-gold-light/40 text-sm font-sans">Redirecting you to your dashboard…</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 2: OTP Verification ──────────────────────────────────────────────
  if (step === 2) {
    return (
      <div className="min-h-screen bg-maroon-dark flex items-center justify-center p-4 pt-24 relative overflow-hidden">
        {background}
        <div className="w-full max-w-md relative z-10">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-10 border border-gold/10 shadow-2xl">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-gold to-gold-dark rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <FaShieldAlt className="text-white text-3xl" />
              </div>
              <h2 className="text-3xl font-serif font-bold text-gold-light mb-2">Verify Your Email</h2>
              <p className="text-gold-light/50 font-sans text-sm">
                We sent a 6-digit code to
              </p>
              <p className="text-gold font-semibold font-sans mt-1 break-all">{formData.email}</p>
            </div>

            <form onSubmit={handleVerifyAndRegister} className="space-y-6">
              {/* OTP Boxes */}
              <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
                {otpInput.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className="w-12 h-14 text-center text-xl font-bold text-white bg-white/5 border border-gold/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold/60 transition-all font-sans"
                    required
                  />
                ))}
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-2xl text-sm font-medium font-sans text-center">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || otpInput.join('').length < 6}
                className="w-full h-14 btn-gold !rounded-2xl flex items-center justify-center space-x-3 text-base"
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    <FaCheckCircle className="text-xl" />
                    <span>Verify &amp; Create Account</span>
                  </>
                )}
              </button>

              <div className="text-center">
                <p className="text-gold-light/40 text-sm font-sans">
                  Didn't receive the code?{' '}
                  <button
                    type="button"
                    onClick={resendOtp}
                    disabled={isSendingOtp}
                    className="text-gold hover:text-gold-light font-semibold transition-colors disabled:opacity-50"
                  >
                    {isSendingOtp ? 'Sending…' : 'Resend OTP'}
                  </button>
                </p>
                <button
                  type="button"
                  onClick={() => { setStep(1); setError(''); setOtpInput(['', '', '', '', '', '']); }}
                  className="mt-2 text-gold-light/30 hover:text-gold-light/60 text-sm font-sans transition-colors"
                >
                  ← Change email address
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 1: Registration Form ─────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-maroon-dark flex items-center justify-center p-4 pt-24 pb-12 relative overflow-hidden">
      <SEO 
        title="Register • Taalsetu"
        description="Join Taalsetu to begin your tabla learning adventure and get access to exclusive lessons from Guru Atharva Kagalkar."
        url="https://www.taalsetu.com/register/"
        noindex
      />
      {background}
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-10 border border-gold/10 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg overflow-hidden bg-white">
              <img src="/images/logo.jpg" alt="Taalsetu Logo" className="w-full h-full object-cover" />
            </div>
            <h2 className="text-4xl font-serif font-bold text-gold-light mb-2 tracking-tight">Join Taalsetu</h2>
            <p className="text-gold-light/50 font-sans font-medium">Begin your tabla learning adventure</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSendOtp} className="space-y-4">
            {/* Name */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gold-light/40 group-focus-within:text-gold transition-colors">
                <FaUser className="h-5 w-5" />
              </div>
              <input
                type="text"
                placeholder="Full Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="block w-full pl-12 pr-4 py-3.5 bg-white/5 border border-gold/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-all text-white placeholder-gold-light/30 font-sans"
                required
              />
            </div>

            {/* Email */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gold-light/40 group-focus-within:text-gold transition-colors">
                <FaEnvelope className="h-5 w-5" />
              </div>
              <input
                type="email"
                placeholder="Email address"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="block w-full pl-12 pr-4 py-3.5 bg-white/5 border border-gold/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-all text-white placeholder-gold-light/30 font-sans"
                required
              />
            </div>

            {/* Password */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gold-light/40 group-focus-within:text-gold transition-colors">
                <FaLock className="h-5 w-5" />
              </div>
              <input
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="block w-full pl-12 pr-4 py-3.5 bg-white/5 border border-gold/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-all text-white placeholder-gold-light/30 font-sans"
                required
              />
            </div>

            {/* Security Question Selection */}
            <div className="relative group">
               <select
                 value={formData.securityQuestion}
                 onChange={(e) => setFormData({ ...formData, securityQuestion: e.target.value })}
                 className="block w-full px-4 py-3.5 bg-maroon-dark border border-gold/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-all text-white font-sans appearance-none"
                 required
               >
                 <option value="" disabled>Select a Security Question</option>
                 <option value="What was your first guru's name?">What was your first guru's name?</option>
                 <option value="What was the name of your first instrument?">What was the name of your first instrument?</option>
                 <option value="In which city were you born?">In which city were you born?</option>
                 <option value="What is your favorite raaga?">What is your favorite raaga?</option>
               </select>
               <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gold/50">
                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
               </div>
            </div>

            {/* Security Answer */}
            <div className="relative group">
              <input
                type="text"
                placeholder="Answer your secret question"
                value={formData.securityAnswer}
                onChange={(e) => setFormData({ ...formData, securityAnswer: e.target.value })}
                className="block w-full px-4 py-3.5 bg-white/5 border border-gold/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-all text-white placeholder-gold-light/30 font-sans"
                required
              />
            </div>

            {/* Password strength checklist */}
            {formData.password.length > 0 && (
              <ul className="space-y-1 px-1">
                {passwordRules.map((rule) => {
                  const ok = rule.test(formData.password);
                  return (
                    <li key={rule.label} className={`flex items-center gap-2 text-xs font-sans transition-colors ${ok ? 'text-green-400' : 'text-red-400/80'}`}>
                      <span>{ok ? '✓' : '✗'}</span>
                      <span>{rule.label}</span>
                    </li>
                  );
                })}
              </ul>
            )}

            {error && (
              <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-2xl text-sm font-medium font-sans">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSendingOtp}
              className="w-full h-14 btn-gold !rounded-2xl flex items-center justify-center space-x-3 text-base mt-6"
            >
              {isSendingOtp ? (
                <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <FaPaperPlane className="text-xl" />
                  <span>Send OTP &amp; Continue</span>
                </>
              )}
            </button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gold/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-maroon-dark text-gold-light/40 rounded-full py-1 font-sans">Already have an account?</span>
            </div>
          </div>

          <Link
            to="/login"
            className="w-full flex items-center justify-center space-x-2 py-4 bg-white/5 border border-gold/10 rounded-2xl text-gold-light font-bold hover:bg-gold/5 transition-all font-sans"
          >
            <FaSignInAlt />
            <span>Sign In</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
